import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  InputLabel,
  LinearProgress,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import DatePicker from '@mui/lab/DatePicker';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { ICoreUser } from '../../../../shared-interfaces/User/ICoreUser';
import { IRetentionStatus } from '../../../../shared-interfaces/User/IRetentionStatus';
import { IUserRetentionStatus } from '../../../../shared-interfaces/User/IUserRetentionStatus';
import { Inclusion } from '../../../../shared-types/Inclusion';
import { Role } from '../../../../shared-types/Roles';
import UserManagementService from '../Services/UserManagementService';
import { UserManagementActionType } from '../Types/UserManagementActionType';
import { UserManagementDispatch, UserManagementState } from '../UserManagement';
import SelectedUserList from './SelectedUserList';
import { UserAttributesContext } from '../../../../shared-components/contexts/UserAttributesContext';

interface IChangeRetentionDialogProps {
  open: boolean;
  close: () => void;
  user?: ICoreUser;
  entryMethod: 'BulkOps' | 'UserList';
  onChange?: (r: IRetentionStatus) => void;
}

interface IChangeRetentionDialogContentProps {
  users: ICoreUser[] | undefined;
  userRetentionStatuses?: IUserRetentionStatus[];
  effectiveDate: Date | null;
  retentionStatus?: IRetentionStatus;
  onRetentionChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
  onEffectiveDateChange: (date: Date | null) => void;
  onUserClear?: (user: ICoreUser) => void;
  entryMethod: 'BulkOps' | 'UserList';
}

export const ChangeRetentionDialogContent = (
  props: IChangeRetentionDialogContentProps,
) => {
  const state = useContext(UserManagementState);
  const userAttributeContext = useContext(UserAttributesContext);

  return (
    <>
      <div style={{ margin: '10px 0 0 0' }}>
        <InputLabel id="retention-select-label">
          For the selected users, change the status to
        </InputLabel>
        <Select
          labelId="retention-select-label"
          id="Retention-select"
          value={props.retentionStatus?.id ?? ''}
          onChange={e => props.onRetentionChange(e as any)}
          style={{ maxWidth: '200px' }}
          renderValue={() => {
            return props.retentionStatus?.name;
          }}
        >
          {userAttributeContext.retentionStatuses.map((x, i) => (
            //@ts-ignore
            <MenuItem key={i} value={x.id} component="div">
              <ListItem dense={true} alignItems="flex-start">
                <ListItemText primary={x.name} secondary={x.description} />
              </ListItem>
            </MenuItem>
          ))}
        </Select>
      </div>
      <div style={{ margin: '35px 0 0 0' }}>
        <InputLabel id="effective-date-label">Effective Date</InputLabel>
        <DatePicker
          renderInput={props => <TextField {...props} />}
          value={props.effectiveDate}
          maxDate={new Date()}
          onChange={(date: Date | null) => props.onEffectiveDateChange(date)}
        />
      </div>
      <div style={{ margin: '35px 0 0 0' }}>
        <InputLabel>Users Impacted</InputLabel>
        <Paper
          variant="outlined"
          style={{
            maxHeight: '200px',
            overflowY: 'scroll',
            overflowX: 'hidden',
            margin: '10px 0 0 0',
          }}
        >
          <SelectedUserList
            actionType="retention"
            users={props.users}
            userRetentionStatuses={props.userRetentionStatuses}
            onClear={props.onUserClear}
          />
        </Paper>
      </div>
      {state.selectAll ? (
        <FormHelperText>
          Users may be omitted. Retention Status changes can only be applied to
          Students and Peer Advisors.
        </FormHelperText>
      ) : (
        (props.users ?? state.selectedUsers).filter(x => {
          return x.role > Role.PeerAdvisor;
        }).length > 0 && (
          <FormHelperText>
            {
              (props.users ?? state.selectedUsers).filter(x => {
                return x.role > Role.PeerAdvisor;
              }).length
            }{' '}
            user(s) omitted. Retention Status changes can only be applied to
            Students and Peer Advisors.
          </FormHelperText>
        )
      )}
    </>
  );
};

const ChangeRetentionDialog = (props: IChangeRetentionDialogProps) => {
  const dispatch = useContext(UserManagementDispatch);
  const state = useContext(UserManagementState);
  const userAttributeContext = useContext(UserAttributesContext);

  const [retentionStatus, setRetentionStatus] = useState<IRetentionStatus>(
    userAttributeContext.retentionStatuses[0],
  );
  const [effectiveDate, setEffectiveDate] = useState<Date | null>(new Date());
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleRetentionChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setRetentionStatus(
      userAttributeContext.retentionStatuses.find(
        x => x.id === event.target.value,
      ) as IRetentionStatus,
    );
  };

  const handleSubmit = () => {
    setIsSubmitting(true);

    let submittedUsers = props.user
      ? [props.user]
      : state.selectAll
      ? state.excludeUsers
      : state.selectedUsers.filter(
          x => x.role === Role.Student || x.role === Role.PeerAdvisor,
        );

    UserManagementService.bulkUpdateUserRetention(
      retentionStatus?.id ?? 0,
      submittedUsers.map(x => x.id),
      props.entryMethod === 'BulkOps' && state.selectAll
        ? Inclusion.Exclude
        : Inclusion.Include,
      effectiveDate ?? new Date(),
      state.filter,
    )
      .then(x => {
        if (props.onChange) {
          props.onChange(
            userAttributeContext.retentionStatuses.find(
              x => x.id === retentionStatus?.id,
            ) as IRetentionStatus,
          );
        } else {
          const changedUsers: ICoreUser[] = [];

          state.users.forEach(u => {
            if (x.findIndex(r => r.userId === u.id) > -1) {
              u.retentionStatusId = retentionStatus?.id;
            }
            changedUsers.push(u);
          });

          dispatch({
            type: UserManagementActionType.SetUsers,
            users: [],
          });

          dispatch({
            type: UserManagementActionType.SetUsers,
            users: changedUsers,
          });
        }
        enqueueSnackbar('Successfully Updated Retention Status', {
          variant: 'success',
        });
        props.close();
      })
      .catch(x => {
        enqueueSnackbar('Failed to Update Retention Status', {
          variant: 'error',
        });
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleUserClear = (user: ICoreUser) => {
    dispatch({
      type: UserManagementActionType.SetSelectedUsers,
      value: state.selectedUsers.filter(u => u.id !== user.id),
    });
  };

  return (
    <>
      <Dialog
        open={props.open}
        onClose={props.close}
        fullWidth={true}
        maxWidth="sm"
      >
        <DialogTitle>Change Retention Status</DialogTitle>
        {props.user ||
        (!state.selectAll &&
          state.selectedUsers.length > 0 &&
          state.selectedUsers.filter(x => {
            return x.role <= Role.PeerAdvisor;
          }).length > 0) ||
        (state.selectAll &&
          (state.filter.roles.length === 0 ||
            state.filter.roles.findIndex(x => x <= Role.PeerAdvisor) !==
              -1)) ? (
          <>
            <DialogContent>
              <ChangeRetentionDialogContent
                users={props.user ? [props.user] : undefined}
                effectiveDate={effectiveDate}
                retentionStatus={retentionStatus}
                onRetentionChange={e => handleRetentionChange(e)}
                onEffectiveDateChange={d => setEffectiveDate(d)}
                onUserClear={u => handleUserClear(u)}
                entryMethod={props.entryMethod}
              />
            </DialogContent>
            <DialogActions>
              <Button
                color="secondary"
                variant="text"
                onClick={() => props.close()}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={isSubmitting && retentionStatus !== undefined}
                onClick={handleSubmit}
              >
                Confirm
              </Button>
            </DialogActions>
            {isSubmitting && <LinearProgress />}
          </>
        ) : (
          <>
            <DialogContent>
              <Box
                height="200px"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                {(state.selectAll &&
                  state.filter.roles.findIndex(x => x <= Role.PeerAdvisor) ===
                    -1) ||
                (!state.selectAll &&
                  state.selectedUsers.filter(x => {
                    return x.role <= Role.PeerAdvisor;
                  }).length === 0) ? (
                  <Typography variant="h2">
                    No applicable users selected. <br />
                    Retention Status changes can only be applied to Students and
                    Peer Advisors.
                  </Typography>
                ) : (
                  <Typography variant="h2">
                    Select user(s) before changing retention status.
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                color="secondary"
                variant="text"
                onClick={() => props.close()}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default ChangeRetentionDialog;
